import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { useStatusTemplate } from '../../../../../../../../api/groups'
import Skeleton from '../../../../../../../atoms/Skeleton'

import ItemMigrationMappingGroup from './ItemMigrationMappingGroup'

function ItemMigrationMapping ({
  statusReport
}) {
  const form = useFormContext()
  const newTemplateId = form.watch('newTemplateId')
  const [mappingsInitialized, setMappingsInitialized] = React.useState(false)

  const { data, isLoading } = useStatusTemplate(newTemplateId)

  const itemsWithData = useMemo(() => {
    if (!data || !statusReport) return null
    return statusReport?.items?.reduce((acc, item) => {
      if (item.threadKey) acc.comments.push(item.codeName)
      if (item.attachmentCount) acc.attachments.push(item.codeName)
      if (item.linkReference) acc.linkReferences.push(item.codeName)
      return acc
    }, {
      comments: [],
      attachments: [],
      linkReferences: []
    })
  }, [data, statusReport])

  const options = useMemo(() => {
    return data?.items?.map(item => ({
      label: item.displayName ?? item.codeName,
      value: item.codeName
    }))
  }, [data?.items])

  // Set default mappings when template data and items are available
  // Only run once when the data becomes available
  useEffect(() => {
    if (!itemsWithData || !options?.length) {
      setMappingsInitialized(false)
      return
    }

    const allKeys = ['comments', 'attachments', 'linkReferences']
    const newMappings = {}

    allKeys.forEach(key => {
      const rows = itemsWithData[key] || []
      if (rows.length) {
        newMappings[key] = {}
        rows.forEach(oldCodeName => {
          const matchingOption = options.find(option => option.value === oldCodeName)
          newMappings[key][oldCodeName] = {
            enabled: true,
            matching: matchingOption?.value ?? ''
          }
        })
      }
    })

    form.setValue('itemMigrationMapping', newMappings, { shouldValidate: false, shouldDirty: true })
    // Small delay to ensure form state is updated before rendering
    setTimeout(() => setMappingsInitialized(true), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsWithData, options, newTemplateId])

  if (isLoading || !mappingsInitialized) {
    return (
      <div className='flex flex-col gap-5'>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} height='140px' />
        ))}
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-5'>
      <ItemMigrationMappingGroup
        label='Comments'
        keyName='comments'
        rows={itemsWithData?.comments}
        options={options}
        statusReport={statusReport}
      />
      <ItemMigrationMappingGroup
        label='Attachments'
        keyName='attachments'
        rows={itemsWithData?.attachments}
        options={options}
        statusReport={statusReport}
      />
      <ItemMigrationMappingGroup
        label='Links'
        keyName='linkReferences'
        rows={itemsWithData?.linkReferences}
        options={options}
        statusReport={statusReport}
      />
    </div>
  )
}

ItemMigrationMapping.propTypes = {
  statusReport: PropTypes.object.isRequired
}

export default ItemMigrationMapping
